import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { PassOrderPageData, PassOut, PassService } from '../../api';
import { BottomSheet } from './BottomSheet';
import { useManagePasses } from '../ManagePassesContext';
import { useLanguage } from '../../utils/GeneralUtils';
import { customerFacingPriceFormatter } from '../../orderPage/utils';
import { withBookingFee } from '../../orderPage/core/pricing';
import { savedCardErrorMessage } from '../utils/savedCardErrors';
import { getPassOrderPageName } from '../utils/passUtils';

type BuyAgainSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  pass: PassOut;
  passOrderPage: PassOrderPageData;
};

export const BuyAgainSheet = ({ isOpen, onClose, pass, passOrderPage }: BuyAgainSheetProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const navigate = useNavigate();
  const { account, email, mutate } = useManagePasses();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setIsLoading(false);
    }
  }, [isOpen]);

  const passName = getPassOrderPageName(passOrderPage, language, t);

  const handleConfirm = async () => {
    if (!email) return;
    setError(null);
    setIsLoading(true);
    try {
      const newPass = await PassService.customerPassBuyAgain({
        email,
        pass_order_number: pass.order_number,
        pass_id: pass.id,
      });
      await mutate();
      onClose();
      // The new pass carries the card token forward, so buying again next time starts from it.
      navigate(`/passes/pass/${newPass.id}`);
    } catch (e: any) {
      Sentry.captureException(e);
      setError(savedCardErrorMessage(e, t));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BottomSheet
      open={isOpen}
      onClose={onClose}
      ctaText={t('manage_passes.buyAgain.confirm')}
      isLoading={isLoading}
      onConfirm={handleConfirm}
      secondaryAction={{ text: t('manage_passes.buyAgain.cancel'), onClick: onClose }}>
      <Grid container flexDirection="column" gap={1}>
        <Typography variant="h6" fontWeight={600}>
          {t('manage_passes.buyAgain.title')}
        </Typography>
        <Typography variant="body1">
          {t('manage_passes.buyAgain.body', {
            passName,
            price: account
              ? customerFacingPriceFormatter(withBookingFee(passOrderPage.price, account), account)
              : passOrderPage.price,
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('manage_passes.buyAgain.chargingNotice')}
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
      </Grid>
    </BottomSheet>
  );
};

export default BuyAgainSheet;
